import React, {useMemo, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import Meeting, {confirmInterviewForTalent, requestRescheduleMeetingForTalentOpp} from "../../models/Meeting";
import {Img} from "react-image";
import LoadingSpinner from "../LoadingSpinner";
import {useAuth} from "../../providers/AuthProvider";
import UserImageCircle from '../UserImageCircle';


const ScheduleMeetingModal = ({onClose, project,...props}) => {

    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();

    const [meeting, setMeeting] = useState(Meeting.make({...props.meeting}));
    const {auth} = useAuth();
    const availableSlots = useMemo(() => {
        return meeting.groupedSlotsAvailableToSelect()
    }, [meeting.available_slots?.length]);

    const selectedDate = useMemo(() => {
        let firstSelect = TB.moment()
        if (TB.isObject(availableSlots) && TB.values(availableSlots).length) {
            firstSelect = TB.moment(TB.first(TB.values(availableSlots)).started_at)
        }
        return firstSelect.startOf("day").toISOString();
    }, [availableSlots]);

    // const [timeSlots, setTimeSlots] = useState(generateTimeSlots(TB.moment(selectedDate).startOf('day')))


    const clientImageSrc = useMemo(() => props.opportunity?.client?.logo_path || TB.defaultUserProfileImagePath(), [props.opportunity?.id])
    // const slotsForSelectedDay = useMemo(() => {
    //     if (Object.keys(availableSlots).length > 0 && TB.moment(selectedDate).isValid()) {
    //         let selectedDay = TB.moment(selectedDate).startOf('day').toISOString();
    //
    //         return availableSlots[selectedDay]
    //     }
    //
    // }, [selectedDate, availableSlots])

    const clientName = props.opportunity?.client?.name;

    const confirmInterviewMutation = useMutation(['confirmInterviewMutation'], selected_slot => confirmInterviewForTalent(
        meeting.id,
        props.activeTalentCandidateId,
        selected_slot
    ), {
            onSuccess: async (meeting) => {
                console.log('onSuccess', meeting)
                setIsLoading(false);
                await onClose();
                await TB.toast("Interview Confirmed", {type: 'success'})
                await TB.wait(200);
                await queryClient.invalidateQueries({
                    predicate: ({queryKey}) => {
                        const params = queryKey[1] || {};

                        return queryKey[0] === 'fetchMeetings' &&
                            params.candidateId === props.activeTalentCandidateId
                    }
                })

                await queryClient.invalidateQueries({
                    predicate: ({queryKey}) => {
                        const params = queryKey[1] || {};

                        return queryKey[0] === 'getConversation' &&
                            params.candidateId === props.activeTalentCandidateId
                    }
                })
            },
            onError: async (e) => {
                console.log('onError', e)
                setIsLoading(false);
                await onClose();
                await TB.showServerErrorToast();
                await TB.wait(200);
                await queryClient.invalidateQueries({
                    predicate: ({queryKey}) => {
                        const params = queryKey[1] || {};

                        return queryKey[0] === 'fetchMeetings' &&
                            params.candidateId === props.activeTalentCandidateId
                    }
                })
            }
        })

    const rescheduleInterviewMutation = useMutation(
        ['rescheduleInterviewMutation'],
        () => requestRescheduleMeetingForTalentOpp(
            props.activeTalentCandidateId,
            meeting.id
        ),
        {
                onSuccess: async (meeting) => {
                    console.log('onSuccess rescheduleInterviewMutation', meeting)
                    setIsLoading(false);
                    await onClose();
                    await TB.toast("Reschedule Request Sent", {type: 'success'})
                    await TB.wait(200);
                    await queryClient.invalidateQueries({
                        predicate: ({queryKey}) => {
                            const params = queryKey[1] || {};

                            return queryKey[0] === 'fetchMeetings' &&
                                params.candidateId === props.activeTalentCandidateId
                        }
                    })

                    await queryClient.invalidateQueries({
                        predicate: ({queryKey}) => {
                            const params = queryKey[1] || {};

                            return queryKey[0] === 'getConversation' &&
                                params.candidateId === props.activeTalentCandidateId
                        }
                    })
                },
                onError: async (e) => {
                    console.log('onError', e)
                    setIsLoading(false);
                    await TB.showServerErrorToast();
                }
            }
    )

    const onMeetingChange = (updatedMeetingProps) => {
        setMeeting(Meeting.make({...meeting, ...updatedMeetingProps}))
    }

    const durations = {
        15: "15 Minutes",
        30: "30 Minutes",
        45: "45 Minutes",
        60: "1 Hour"
    }

    const scheduleHeader = useMemo(() => {
        if (meeting.rescheduled_at) {
            return `${clientName} has updated the interview and proposed new times. Select a time to confirm.`
        }
        return `${clientName} has requested an interview. Select a time to confirm.`
        }, [meeting, clientName],
    );

    const currentAuthTime = TB.currentTimezone();

    return (
        <div className={TB.mapClass("scheduler-container talent_NEW", {isLoading})}>

            <div className="flex flex-col form-gap-24">
                <h4 className="scheduler-header">
                    {scheduleHeader}
                </h4>

                <div className="flex flex-row items-center justify-start">
                    <Img
                        loader={(
                            <div className="loading-user-image">
                                <LoadingSpinner/>
                            </div>
                        )}
                        src={clientImageSrc}
                        className="img-fluid"
                    />

                    <div className="interview-candidate-info">
                        <p>{clientName}</p>

                        <div className="interview-candidate-info-options">
                            {props.opportunity.project_position?.title && (
                                <p>
                                    <i className="fa fa-info-circle"/>
                                    {props.opportunity.project_position.title}
                                </p>
                            )}
                            {props.opportunity.project?.location?.city && (
                                <p>
                                    <i className="fa fa-map-marker-alt"/>
                                    {props.opportunity.project.location.city}
                                    {props.opportunity.project.location.state && (<span>, {props.opportunity.project.location.state}</span>)}
                                </p>
                            )}

                        </div>
                    </div>
                </div>

                <div className="flex flex-row flex-wrap form-gap-24">
                    <div className="flex flex-col form-gap-24 lg:w-4/12 p-0" style={{minWidth: 260}}>
                        <div className="form-group subject-text">
                            <label className="col-form-label">Subject</label>
                            <p className="m-0">{meeting.name}</p>
                        </div>
                        <div className="form-group">
                            <label className="col-form-label">Organizer</label>
                            <div className="flex items-center">

                                <UserImageCircle
                                    user={meeting.creator}
                                    hideTooltip={true}
                                    className="img-grayscale"
                                    size={32}
                                />
                                <p className="name whitespace-no-wrap pl-2 m-0">
                                    {meeting.creator?.firstNamePlusInitial()}
                                </p>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="col-form-label">Meeting Link/Details</label>
                            <div dangerouslySetInnerHTML={{__html: meeting.conference_link || '<span class="text-italic">TBD</span>'}}></div>
                        </div>

                        <div className="form-group">
                            <label className="col-form-label">Timezone</label>
                            <p className="m-0">{meeting.created_timezone}</p>
                        </div>

                        <div className="form-group">
                            <label className="col-form-label">Duration</label>
                            <p className="m-0">{durations[meeting.duration] || durations[60]}</p>
                        </div>
                    </div>

                    <div className="flex-grow flex flex-col form-gap-24 lg:w-8/12 p-0" style={{flex: 0, minWidth: 400}}>
                        <div>
                            <div className="form-group">
                                <div className="flex items-center pb-2">
                                    <label className="col-form-label">Select a date and time</label>

                                    {/* <p className="m-0 pr-2">Select a date and time</p> */}
                                    {/* <label className="col-form-label text-italic"
                                           title={meeting.created_timezone}
                                    >

                                        (Times are in shown {currentAuthTime}
                                        {meeting.differentTimezoneThanAuth() && (
                                            <span> but client has requested times in {meeting.timeZoneFormat()}</span>
                                        )})
                                    </label> */}
                                </div>
                            </div>

                            <div className="selected-slots-container">

                                {TB.map(availableSlots, (slots, day) => {
                                    return (
                                        <div key={"selected-" + day}>
                                            <p className="selected-slot-label">{TB.applyTimezoneToDateTime(day, meeting.created_timezone).format("ddd, MMM Do, YYYY")}</p>
                                            <div className="flex flex-row flex-wrap" style={{gap: 9}}>
                                                {TB.map(slots, slot => {
                                                    const timezone = TB.getTimezoneAbbrFromOption(slot.started_at,meeting.created_timezone)
                                                    const startingTime = TB.applyTimezoneToDateTime(slot.started_at, meeting.created_timezone);
                                                    const endingTime = startingTime.clone().add(meeting.duration, 'minutes');
                                                    return (
                                                        <button
                                                            key={slot.started_at}
                                                            type="button"
                                                            className={TB.mapClass("slot_NEW slot-selected-talent", {
                                                                disabled: TB.moment().isAfter(startingTime),
                                                                selected: slot.started_at === meeting.started_at
                                                            })}
                                                            onClick={() => onMeetingChange({ started_at: slot.started_at, ended_at: slot.ended_at })}
                                                        >
                                                            {startingTime.format("h:mma")} - {endingTime.format('h:mma')} {timezone}
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>


                        <div className="flex flex-row flex-no-wrap items-center justify-center">
                            <p className="pr-2 mb-0">Unavailable at these times?</p>

                            <button
                                className="btn btn-outline-dark"
                                onClick={() => {
                                    setIsLoading(true);
                                    rescheduleInterviewMutation.mutate();
                                }}
                            >
                                {/* Submit a request to reschedule for another date */}
                                Request A Reschedule
                            </button>
                        </div>
                    </div>
                </div>

            </div>


            <div className="submit-container">
                <div>
                    <button
                        onClick={() => {
                            setIsLoading(true);
                            confirmInterviewMutation.mutate({
                                started_at: meeting.started_at,
                                ended_at: meeting.ended_at
                            })
                        }}
                        className="btn btn-primary"
                        disabled={!meeting.started_at}
                    >
                        Confirm Interview
                    </button>
                </div>
                <div>
                    <button className="btn btn-outline-dark" onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>

            {isLoading && (
                <div className="loading-container">
                    <LoadingSpinner size={"lg"} />
                </div>
            )}
        </div>
    );
};

ScheduleMeetingModal.propTypes = {};

export default ScheduleMeetingModal;